import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, doc, updateDoc, serverTimestamp, limit } from 'firebase/firestore';
import { onSnapshot } from '../utils/firestoreListen';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import ArtworkReactions from './ArtworkReactions';
import { getReactionArtworkId } from '../utils/reactionArtworkId';
import { clearCache, createCacheKey } from '../utils/cache';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import { useInfiniteOlderHistory } from '../hooks/useInfiniteOlderHistory';
import { PAGING } from '../config/paging';
import { trackEvent, logError } from '../utils/observability';
import { API_ENDPOINTS } from '../config/backendConfig';
import { resolveImageUrl as resolveImageUrlUtil, warmResolvedImageUrls } from '../utils/urlResolver';
import SmartImage from './SmartImage';
import { FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import {
  coLearningExpandedBackdropProps,
  coLearningInkBody,
  coLearningInkTitle,
  coLearningNestedBackdropProps,
  coLearningWallBackdropProps,
} from '../ui/coLearningModalTheme';
import CoLearningPromptBackdrop from '../ui/CoLearningPromptBackdrop';
import { useArtistSalonAgeFilter } from '../context/ArtistSalonAgeFilterContext';

const resolveArtworkPreviewUrl = (item = {}) =>
  resolveImageUrlUtil(item?.thumbnailUrl || item?.imageUrl || item?.artworkUrl || item?.transformedImageUrl || item?.originalImageUrl || '');

const addProxyFallbackOnError = (event) => {
  try {
    const img = event.currentTarget;
    const current = img?.src || '';
    if (!current || current.includes('/proxy-image?')) {
      img.style.display = 'none';
      const fallback = img.parentElement?.querySelector('.image-fallback');
      if (fallback) fallback.style.display = 'flex';
      return;
    }
    if (current.startsWith('http://') || current.startsWith('https://')) {
      const api = require('../config/backendConfig').API_ENDPOINTS;
      img.src = `${api.backendBase}/proxy-image?url=${encodeURIComponent(current)}`;
      return;
    }
    img.style.display = 'none';
    const fallback = img.parentElement?.querySelector('.image-fallback');
    if (fallback) fallback.style.display = 'flex';
  } catch (_) {
    const img = event.currentTarget;
    img.style.display = 'none';
    const fallback = img.parentElement?.querySelector('.image-fallback');
    if (fallback) fallback.style.display = 'flex';
  }
};

const artHistorySnapshotKey = (uid) => `co-learning:art-history:${uid || 'anon'}`;

const collectActivityImageUrls = (activity) => {
  const urls = [];
  const artworkUrl = resolveArtworkPreviewUrl(activity?.artworkMeta || {});
  if (artworkUrl) urls.push(artworkUrl);

  const flowMeta = activity?.flowActivityMeta || null;
  if (flowMeta?.image1Url) urls.push(resolveImageUrlUtil(flowMeta.image1Url));
  if (flowMeta?.image2Url) urls.push(resolveImageUrlUtil(flowMeta.image2Url));

  (activity?.submissions || []).forEach((submission) => {
    const url = resolveArtworkPreviewUrl(submission);
    if (url) urls.push(url);
  });

  return Array.from(new Set(urls)).filter(Boolean);
};

const getPrimaryArtworkForLearnFlow = (activityOrSubmission) => {
  try {
    const activity = activityOrSubmission || {};
    const firstSubmission = activity?.submissions?.[0] || activity;

    const artId =
      firstSubmission?.artworkId ||
      activity?.artworkMeta?.id ||
      activity?.artworkKey ||
      '';

    const imageUrl =
      firstSubmission?.artworkImageUrl ||
      firstSubmission?.artworkMeta?.imageUrl ||
      activity?.artworkMeta?.imageUrl ||
      firstSubmission?.imageUrl ||
      '';

    const title =
      activity?.artworkMeta?.title ||
      firstSubmission?.artworkMeta?.title ||
      firstSubmission?.artworkMeta?.movement ||
      'Art History';

    // LearnArtHistoryFlow will derive artworkTypeKey/level if artworkTypeKey/level isn't provided.
    return {
      id: artId,
      imageUrl,
      title,
      originalImageUrl: imageUrl,
    };
  } catch (_) {
    return { id: '', imageUrl: '', title: 'Art History', originalImageUrl: '' };
  }
};

const ArtHistoryCoLearningSpace = () => {
  const { filterSubmissions } = useArtistSalonAgeFilter();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [artHistoryActivities, setArtHistoryActivities] = useState(() => {
    try {
      const raw = sessionStorage.getItem(artHistorySnapshotKey(user?.uid));
      return raw ? JSON.parse(raw) : {};
    } catch (_) {
      return {};
    }
  });
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [recentParticipations, setRecentParticipations] = useState(new Set());
  const [recentShareStatusByKey, setRecentShareStatusByKey] = useState({});
  const [olderParticipations, setOlderParticipations] = useState(new Set());
  const [olderShareStatusByKey, setOlderShareStatusByKey] = useState({});
  const [lockedActivity, setLockedActivity] = useState(null);
  const [sharePrompt, setSharePrompt] = useState(null);
  const [error, setError] = useState(null);
  const recentOldestDocRef = useRef(null);

  useEffect(() => {
    try {
      if (!user?.uid) return;
      const raw = sessionStorage.getItem(artHistorySnapshotKey(user.uid));
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
        setArtHistoryActivities(parsed);
      }
    } catch (_) {}
  }, [user?.uid]);

  // Paginated loader for public art history submissions (grid content)
  const publicQuery = useMemo(
    () =>
      query(
        collection(db, 'artHistorySharedArtworks'),
        where('visibility', '==', 'public'),
        orderBy('createdAt', 'desc')
      ),
    []
  );

  const {
    docs: publicDocs,
    loading: publicLoading,
    loadingMore: publicLoadingMore,
    error: publicError,
    hasMore: publicHasMore,
    loadMore: loadMorePublic,
    refresh: refreshPublic,
  } = usePaginatedQuery({ baseQuery: publicQuery, pageSize: PAGING.ART_HISTORY_PUBLIC_PAGE_SIZE });

  // When publicDocs change, regroup into activities for the grid (inline to avoid TDZ)
  useEffect(() => {
    try {
      const submissions = publicDocs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const activities = {};
      const sorted = [...submissions].sort((a, b) => {
        let aTime = 0;
        let bTime = 0;
        if (a.createdAt?.toMillis) aTime = a.createdAt.toMillis();
        else if (a.createdAt?._seconds) aTime = a.createdAt._seconds * 1000 + (a.createdAt._nanoseconds || 0) / 1000000;
        else if (a.createdAt instanceof Date) aTime = a.createdAt.getTime();
        if (b.createdAt?.toMillis) bTime = b.createdAt.toMillis();
        else if (b.createdAt?._seconds) bTime = b.createdAt._seconds * 1000 + (b.createdAt._nanoseconds || 0) / 1000000;
        else if (b.createdAt instanceof Date) bTime = b.createdAt.getTime();
        return bTime - aTime;
      });
      sorted.forEach((submission) => {
        let groupKey = 'unknown';
        const artworkMeta = submission.artworkMeta || {};
        const flowMeta = submission.flowActivityMeta || null;
        const typeKeyFromFlow = flowMeta?.artworkTypeKey;
        const typeKeyFromArtwork = artworkMeta?.artworkTypeKey;
        if (typeKeyFromFlow || typeKeyFromArtwork) {
          const key = typeKeyFromFlow || typeKeyFromArtwork;
          const level = flowMeta?.level || artworkMeta?.level || '';
          groupKey = `type:${key}:L${level}`;
        } else {
          groupKey = submission.artworkId || submission.artworkImageUrl || 'unknown';
        }
        if (!activities[groupKey]) {
          activities[groupKey] = {
            artworkKey: groupKey,
            artworkMeta,
            flowActivityMeta: flowMeta,
            submissions: [],
            totalSubmissions: 0
          };
        }
        if (flowMeta && !activities[groupKey].flowActivityMeta) {
          activities[groupKey].flowActivityMeta = flowMeta;
        }
        if (submission.visibility === 'public') {
          activities[groupKey].submissions.push(submission);
          activities[groupKey].totalSubmissions++;
        }
      });

      setArtHistoryActivities(activities);
      setError(publicError ? 'Failed to load activities. Please try again.' : null);
      setLoading(publicLoading && submissions.length === 0);
    } catch (err) {
      console.error('[ArtHistoryCoLearningSpace] Error processing paginated public docs:', err);
      setError('Failed to load activities. Please try again.');
      setLoading(false);
    }
  }, [publicDocs, publicError, publicLoading]);

  // Persist and warm top preview images for instant subsequent paints.
  useEffect(() => {
    try {
      if (!user?.uid) return;
      if (!artHistoryActivities || Object.keys(artHistoryActivities).length === 0) return;
      sessionStorage.setItem(
        artHistorySnapshotKey(user.uid),
        JSON.stringify(artHistoryActivities)
      );
      const previewUrls = Object.values(artHistoryActivities)
        .flatMap((a) => (a?.submissions || []).slice(0, 1))
        .map((s) => resolveArtworkPreviewUrl(s))
        .filter(Boolean)
        .slice(0, 24);
      warmResolvedImageUrls(previewUrls, 24).catch(() => {});
    } catch (_) {}
  }, [user?.uid, artHistoryActivities]);

  // Infinite scroll for older user history (beyond realtime 200-window)
  const {
    olderDocs,
    loadingOlder,
    hasMoreOlder,
    sentinelRef: olderHistorySentinelRef,
  } = useInfiniteOlderHistory({
    userId: user?.uid || null,
    recentOldestDoc: recentOldestDocRef.current,
    pageSize: 50,
  });

  // Derive participations/share status from olderDocs (paged via getDocs)
  useEffect(() => {
    if (!user) {
      setOlderParticipations(new Set());
      setOlderShareStatusByKey({});
      return;
    }

    const participations = new Set();
    const shareStatus = {};

    olderDocs.forEach((docSnap) => {
      const data = docSnap.data();
      const artworkKey = data.artworkId || data.artworkImageUrl || 'unknown';
      participations.add(artworkKey);

      if (!shareStatus[artworkKey]) {
        shareStatus[artworkKey] = { hasPublic: false };
      }
      if (data.visibility === 'public') {
        shareStatus[artworkKey].hasPublic = true;
        // For older history, latestId is mainly used for "share now" actions; recent window is authoritative.
        if (!shareStatus[artworkKey].latestId) {
          shareStatus[artworkKey].latestId = docSnap.id;
        }
      }
    });

    setOlderParticipations(participations);
    setOlderShareStatusByKey(shareStatus);
  }, [olderDocs, user]);

  // Combine recent (realtime) and older (paged) state.
  // Recent window is authoritative for newest items and share status.
  const userParticipations = useMemo(() => {
    const combined = new Set(olderParticipations);
    for (const key of recentParticipations) {
      combined.add(key);
    }
    return combined;
  }, [olderParticipations, recentParticipations]);

  const shareStatusByKey = useMemo(
    () => ({
      ...olderShareStatusByKey,
      ...recentShareStatusByKey,
    }),
    [olderShareStatusByKey, recentShareStatusByKey]
  );

  // Real-time listener ONLY for the current user's own artworks (for participations and share status).
  // Public grid content is loaded via paginated one-time reads (see usePaginatedQuery above).
  useEffect(() => {
    if (!user) {
      console.log('[ArtHistoryCoLearningSpace] No user, clearing user-specific state');
      setRecentParticipations(new Set());
      setRecentShareStatusByKey({});
      setOlderParticipations(new Set());
      setOlderShareStatusByKey({});
      recentOldestDocRef.current = null;
      return () => {}; // Always return a cleanup function
    }

    console.log('[ArtHistoryCoLearningSpace] Setting up real-time listener for user:', user.uid);
    let unsubscribeUser = () => {}; // Default no-op function

    try {
      // Real-time listener for user's own artworks (for participations), capped to recent items
      // Prefer uid + createdAt index; fallback query avoids failed-precondition when index is building.
      const userQueryWithOrder = query(
        collection(db, 'artHistorySharedArtworks'),
        where('uid', '==', user.uid),
        orderBy('createdAt', 'desc'),
        limit(PAGING.USER_HISTORY_RECENT_LIMIT)
      );
      const userQueryFallback = query(
        collection(db, 'artHistorySharedArtworks'),
        where('uid', '==', user.uid),
        limit(PAGING.USER_HISTORY_RECENT_LIMIT)
      );

      const processUserSnapshot = (snapshot) => {
            try {
              const participations = new Set();
              const shareStatus = {};

              const docs = [...snapshot.docs].sort((a, b) => {
                const ta = a.data()?.createdAt?.toMillis?.() ?? 0;
                const tb = b.data()?.createdAt?.toMillis?.() ?? 0;
                return tb - ta;
              });
              if (docs.length > 0) {
                recentOldestDocRef.current = docs[docs.length - 1];
              } else {
                recentOldestDocRef.current = null;
              }

              docs.forEach((docSnap) => {
                const data = docSnap.data();
                const artworkKey = data.artworkId || data.artworkImageUrl || 'unknown';
                participations.add(artworkKey);

                if (!shareStatus[artworkKey]) {
                  shareStatus[artworkKey] = { hasPublic: false };
                }
                if (data.visibility === 'public') {
                  shareStatus[artworkKey].hasPublic = true;
                  shareStatus[artworkKey].latestId = docSnap.id;
                }
              });

              console.log('✅ [ART HISTORY] User participations updated:', {
                participationsCount: participations.size,
                shareStatusCount: Object.keys(shareStatus).length,
              });

              setRecentParticipations(participations);
              setRecentShareStatusByKey(shareStatus);
            } catch (err) {
              console.error('[ArtHistoryCoLearningSpace] Error processing user artworks:', err);
            }
      };

      const attachUserListener = (q, isFallback = false) => {
        unsubscribeUser = onSnapshot(
          q,
          processUserSnapshot,
          (error) => {
            if (
              !isFallback &&
              error?.code === 'failed-precondition' &&
              String(error?.message || '').includes('index')
            ) {
              console.warn(
                '[ArtHistoryCoLearningSpace] Missing Firestore index for uid+createdAt; using fallback listener'
              );
              attachUserListener(userQueryFallback, true);
              return;
            }
            console.error('[ArtHistoryCoLearningSpace] Error in user artworks listener:', error);
            setRecentParticipations(new Set());
            setRecentShareStatusByKey({});
          },
          { purpose: 'ArtHistoryCoLearningSpace-user-participations', component: 'ArtHistoryCoLearningSpace' }
        );
      };

      try {
        attachUserListener(userQueryWithOrder, false);
        
        // Ensure unsubscribeUser is a function
        if (!unsubscribeUser || typeof unsubscribeUser !== 'function') {
          console.warn('[ArtHistoryCoLearningSpace] onSnapshot did not return a function for user query');
          unsubscribeUser = () => {}; // No-op function
        }
      } catch (snapshotError) {
        console.error('[ArtHistoryCoLearningSpace] Error setting up user snapshot:', snapshotError);
        unsubscribeUser = () => {}; // No-op function
      }
    } catch (err) {
      console.error('[ArtHistoryCoLearningSpace] Error setting up user listener:', err);
      setRecentParticipations(new Set());
      setRecentShareStatusByKey({});
      unsubscribeUser = () => {};
    }

    // Cleanup listener on unmount or user change
    return () => {
      console.log('[ArtHistoryCoLearningSpace] Cleaning up user real-time listener');
      if (unsubscribeUser && typeof unsubscribeUser === 'function') {
        try {
          unsubscribeUser();
        } catch (err) {
          console.error('[ArtHistoryCoLearningSpace] Error unsubscribing user listener:', err);
        }
      }
    };
  }, [user]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    const previousBg = document.body.style.backgroundColor;
    if (isOverlayOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.backgroundColor = '#fef3c7';
    } else {
      document.body.style.overflow = previous || '';
      document.body.style.backgroundColor = previousBg || '';
    }
    return () => {
      document.body.style.overflow = previous || '';
      document.body.style.backgroundColor = previousBg || '';
    };
  }, [isOverlayOpen]);

  // Backward compatibility function (no longer needed, but kept for compatibility)
  const loadArtHistoryActivities = () => {
    console.log('[ArtHistoryCoLearningSpace] loadArtHistoryActivities called - using real-time listeners instead');
    // Real-time listeners handle updates automatically, no manual reload needed
  };

  const makeKey = (artworkKey) => artworkKey;

  // Backward compatibility functions (no longer needed, but kept for compatibility)
  const loadUserParticipations = async () => {
    console.log('[ArtHistoryCoLearningSpace] loadUserParticipations called - using real-time listeners instead');
    // Real-time listeners handle updates automatically
  };

  const loadUserShareStatus = async (keys) => {
    console.log('[ArtHistoryCoLearningSpace] loadUserShareStatus called - using real-time listeners instead');
    // Real-time listeners handle updates automatically
  };

  const warmActivityImages = useCallback((activity) => {
    const urls = collectActivityImageUrls(activity);
    if (!urls.length) return;
    warmResolvedImageUrls(urls, 40).catch(() => {});
  }, []);

  const handleActivityClick = (activity) => {
    warmActivityImages(activity);
    const key = makeKey(activity.artworkKey);
    const hasContributed = (activity.submissions || []).some(
      (submission) => submission?.uid === user?.uid || submission?.userId === user?.uid
    );
    const hasParticipated = userParticipations.has(key) || hasContributed;
    if (!hasParticipated) {
      setLockedActivity(activity);
      setIsOverlayOpen(true);
      return;
    }
    const share = shareStatusByKey[key];
    if (share?.hasPublic || hasContributed) {
      setSelectedActivity(activity);
      setIsOverlayOpen(true);
    } else {
      setSharePrompt({ key, latestId: share?.latestId || null, artworkKey: activity.artworkKey });
      setIsOverlayOpen(true);
    }
  };

  const handleCloseModal = () => {
    setSelectedActivity(null);
    setIsOverlayOpen(false);
  };

  const closeLocked = () => {
    setLockedActivity(null);
    setIsOverlayOpen(false);
  };

  const confirmShareNow = async () => {
    try {
      if (!sharePrompt || !sharePrompt.latestId) {
        setSharePrompt(null);
        setIsOverlayOpen(false);
        return;
      }
      const docRef = doc(db, 'artHistorySharedArtworks', sharePrompt.latestId);
      await updateDoc(docRef, {
        visibility: 'public',
        updatedAt: serverTimestamp()
      });
      trackEvent('art_history_submission_published', {
        docId: sharePrompt.latestId,
        artworkKey: sharePrompt.key,
        userId: user?.uid || null,
      });
      // Real-time listeners will automatically update the UI
      setSharePrompt(null);
      setIsOverlayOpen(false);
      // Clear cache if needed (though real-time listeners handle updates)
      const cacheKey = createCacheKey('artHistoryShared', { uid: user.uid }, user.uid);
      clearCache(cacheKey);
    } catch (e) {
      console.error('Error sharing art history artwork:', e);
      alert('Failed to share artwork. Please try again.');
      logError(e, {
        where: 'ArtHistoryCoLearningSpace.confirmShareNow',
        artworkKey: sharePrompt?.key,
        userId: user?.uid || null,
      });
    }
  };

  useEffect(() => {
    if (!loading) setHasLoadedOnce(true);
  }, [loading]);

  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
        </div>
        <div className="text-center py-12">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md mx-auto">
            <div className="text-red-600 text-4xl mb-4">⚠️</div>
            <h3 className="text-xl font-semibold text-red-800 mb-2">Error Loading Activities</h3>
            <p className="text-red-700 mb-6">{error}</p>
            <button
              onClick={() => {
                setError(null);
                // Real-time listeners will automatically retry
                window.location.reload();
              }}
              className="bg-gradient-to-r from-amber-500 to-amber-400 text-white px-6 py-3 rounded-full hover:from-amber-600 hover:to-amber-500 transition"
            >
              Reload Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show all public Art History submissions (flow and non-flow activities).
  const visibleActivities = Object.values(artHistoryActivities).filter(
    (a) => filterSubmissions(a.submissions || []).length > 0
  );
  const isRefreshing = loading || publicLoading;

  return (
    <div className={`space-y-3 ${isOverlayOpen ? 'overflow-hidden' : ''}`}>
      {/* Intentionally no explicit loading UI: keep experience instant */}
      {/* Description text removed - now shown in parent GalleryStudio component above */}

      {visibleActivities.length === 0 && !isRefreshing ? (
        <div className="text-center py-12">
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            No Art History Activities Yet
          </h3>
          <p className="text-gray-700 mb-6">
            No artworks have been shared for art history activities yet.
          </p>
          <button
            onClick={() => navigate('/learn-art-history')}
            className="bg-gradient-to-r from-amber-500 to-amber-400 text-white px-6 py-3 rounded-full hover:from-amber-600 hover:to-amber-500 transition"
          >
            Start Art History Appreciation
          </button>
        </div>
      ) : (
        <>
        <div
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${isOverlayOpen ? 'pointer-events-none' : ''}`}
          data-testid="art-history-grid"
        >
          {visibleActivities.map((activity) => {
            const publicSubs = filterSubmissions(activity.submissions || []);
            const key = makeKey(activity.artworkKey);
            const hasContributed = (activity.submissions || []).some(
              (submission) => submission?.uid === user?.uid || submission?.userId === user?.uid
            );
            const hasParticipated = userParticipations.has(key) || hasContributed;
            const isUnlocked = !!shareStatusByKey[key]?.hasPublic || hasContributed;
            const artworkMeta = activity.artworkMeta || {};
            const flowMeta = activity.flowActivityMeta || null;
            const isFlowActivity = !!flowMeta;
            
            return (
              <motion.div
                key={activity.artworkKey}
                className={`cursor-pointer rounded-2xl border border-amber-200/60 bg-gradient-to-br from-amber-50/90 to-orange-50/90 p-4 shadow-lg backdrop-blur-sm sm:p-5 ${hasParticipated ? '' : 'opacity-75'}`}
                onClick={() => handleActivityClick(activity)}
                onMouseEnter={() => warmActivityImages(activity)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1">
                    <button
                      type="button"
                      className="text-left text-lg font-bold text-amber-800 mb-1 hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        const primaryArtwork = getPrimaryArtworkForLearnFlow(activity);
                        navigate('/learn-art-history/flow', {
                          state: {
                            artwork: primaryArtwork,
                            primaryArtwork,
                            openModal: false,
                            embeddedInFlow: true,
                            fromLearnArtHistory: true,
                          },
                        });
                      }}
                    >
                      {isFlowActivity ? (flowMeta.title || 'Flow Activity') : (artworkMeta.title || 'Art History Artwork')}
                    </button>
                    {!isFlowActivity && (
                      <>
                        {artworkMeta.artist && !/^unknown/i.test(String(artworkMeta.artist).trim()) && (
                          <p className="text-sm text-amber-600 mb-1">
                            {artworkMeta.artist}
                          </p>
                        )}
                        {artworkMeta.movement && (
                          <span className="text-xs bg-amber-200 text-amber-800 px-2 py-1 rounded-full">
                            {artworkMeta.movement}
                          </span>
                        )}
                      </>
                    )}
                    <p className="text-xs text-amber-600 mt-2">
                      {publicSubs.length} artwork{publicSubs.length !== 1 ? 's' : ''} shared
                    </p>
                  </div>
                  {hasParticipated && isUnlocked ? (
                    <div className="text-green-600" title="Unlocked: You have shared your artwork">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                    </div>
                  ) : (
                    <div className="text-amber-500" title={hasParticipated ? 'Locked: Share to unlock' : 'Complete to unlock'}>
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
                    </div>
                  )}
                </div>

                {/* Left: Activity (single artwork or flow = two images + title + objective). Right: User submissions preview */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="relative">
                    {isFlowActivity ? (
                      <div className={`w-full h-32 rounded-lg border-2 border-amber-200 overflow-hidden bg-amber-50 ${!isUnlocked ? 'filter blur-sm' : ''}`}>
                        <div className="grid grid-cols-2 gap-0.5 h-full">
                          {flowMeta.image1Url && (
                            <div className="relative h-full min-h-0">
                              <SmartImage src={flowMeta.image1Url} alt={flowMeta.title1 || 'Artwork 1'} className="w-full h-full object-cover" containerClassName="w-full h-full" />
                            </div>
                          )}
                          {flowMeta.image2Url && (
                            <div className="relative h-full min-h-0">
                              <SmartImage src={flowMeta.image2Url} alt={flowMeta.title2 || 'Artwork 2'} className="w-full h-full object-cover" containerClassName="w-full h-full" />
                            </div>
                          )}
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[10px] px-2 py-1 truncate" title={flowMeta.title}>
                          {flowMeta.title}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className={`w-full h-32 rounded-lg border-2 border-amber-200 overflow-hidden bg-amber-50 ${!isUnlocked ? 'filter blur-sm' : ''}`}>
                          {resolveArtworkPreviewUrl(artworkMeta) ? (
                            <SmartImage
                              src={resolveArtworkPreviewUrl(artworkMeta)}
                              alt={artworkMeta.title || 'Art History Artwork'}
                              className="w-full h-full object-cover"
                              containerClassName="w-full h-full"
                              onError={addProxyFallbackOnError}
                            />
                          ) : null}
                          <div className="image-fallback hidden absolute inset-0 items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50">
                            <div className="text-center">
                              <div className="text-2xl mb-1">🎨</div>
                              <p className="text-xs font-medium text-amber-700">Art History</p>
                            </div>
                          </div>
                        </div>
                        <div className="absolute top-1 left-1 bg-black/60 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
                          Original
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* User Submissions Preview - Right side scroller */}
                  <div className="relative">
                    <div className={`w-full h-32 rounded-lg border-2 border-amber-200 overflow-hidden bg-amber-50 ${!isUnlocked ? 'filter blur-sm' : ''}`}>
                      {resolveArtworkPreviewUrl(publicSubs[0]) ? (
                        <img
                          src={resolveArtworkPreviewUrl(publicSubs[0])}
                          alt="User submission"
                          className="w-full h-full object-cover"
                          onError={addProxyFallbackOnError}
                        />
                      ) : (
                        <div className="absolute inset-0 items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50 flex">
                          <div className="text-center">
                            <div className="text-2xl mb-1">👤</div>
                            <p className="text-xs font-medium text-amber-700">User Art</p>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="absolute top-1 right-1 bg-black/60 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
                      {publicSubs.length} shared
                    </div>
                  </div>
                </div>

                {!isUnlocked && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="bg-black/30 rounded-full px-3 py-1 text-white text-xs font-semibold flex items-center gap-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/></svg>
                      Locked: Complete and share to unlock
                    </div>
                  </div>
                )}

                {/* Action Button */}
                <div className="text-center mt-4">
                  {!hasParticipated && (
                    <button className="bg-gray-300 text-gray-600 font-semibold py-2 px-6 rounded-full cursor-not-allowed">Complete to Unlock</button>
                  )}
                  {hasParticipated && !isUnlocked && (
                    <button className="bg-amber-200 text-amber-800 font-semibold py-2 px-6 rounded-full">Share to Unlock</button>
                  )}
                  {hasParticipated && isUnlocked && (
                    <button className="rounded-full bg-gradient-to-r from-[#F59E0B] to-[#F97316] px-4 py-1.5 text-xs font-semibold text-white shadow-[0_6px_18px_rgba(234,88,12,0.22)] transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(234,88,12,0.28)] sm:px-5 sm:py-2 sm:text-sm">Enter Gallery</button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
        {publicHasMore && (
          <div className="flex justify-center mt-6">
            <button
              type="button"
              onClick={loadMorePublic}
              disabled={publicLoadingMore}
              className="px-6 py-2 rounded-full bg-gradient-to-r from-amber-500 to-orange-400 text-white text-sm font-semibold shadow hover:from-amber-600 hover:to-orange-500 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {publicLoadingMore ? 'Loading more activities...' : 'Load more activities'}
            </button>
          </div>
        )}

        {/* Infinite-scroll sentinel & subtle loader for older user history (userParticipations) */}
        <div
          ref={olderHistorySentinelRef}
          className="mt-4 flex justify-center items-center min-h-[24px]"
        >
          {loadingOlder && (
            <div className="flex items-center gap-2 text-xs text-amber-600">
              <span className="inline-block w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
              <span>Loading more of your history…</span>
            </div>
          )}
          {!loadingOlder && !hasMoreOlder && (
            <div className="text-[10px] text-amber-500">
              {/* Reserve space to avoid layout shift when finished */}
            </div>
          )}
        </div>
        </>
      )}

      {/* Locked Activity Modal */}
      {lockedActivity && (
        <CoLearningPromptBackdrop>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="text-6xl mb-4">🔒</div>
              <h3 className={`text-2xl mb-4 ${coLearningInkTitle}`}>Activity Locked</h3>
              <p className={`mb-6 ${coLearningInkBody}`}>
                Complete an Art History Appreciation activity and share your artwork to unlock and see the works by other artists for this artwork.
              </p>
              <div className="flex gap-4 justify-center">
                <button onClick={closeLocked} className="bg-gray-200 text-gray-800 font-semibold py-3 px-6 rounded-full">Close</button>
                  <button
                    onClick={() => {
                      const primaryArtwork = getPrimaryArtworkForLearnFlow(lockedActivity);
                      closeLocked();
                      navigate('/learn-art-history/flow', {
                        state: {
                          artwork: primaryArtwork,
                          primaryArtwork,
                          openModal: false,
                          embeddedInFlow: true,
                          fromLearnArtHistory: true,
                        },
                      });
                    }}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold py-3 px-6 rounded-full"
                  >
                    Start Flow
                  </button>
              </div>
            </div>
          </div>
        </CoLearningPromptBackdrop>
      )}

      {/* Share-to-Unlock Prompt */}
      {sharePrompt && (
        <CoLearningPromptBackdrop>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="text-6xl mb-4">🔓</div>
              <h3 className={`text-2xl mb-4 ${coLearningInkTitle}`}>Share to Unlock</h3>
              <p className={`mb-6 ${coLearningInkBody}`}>Share your artwork for this art history artwork to unlock the co-learning space and see what other artists created.</p>
              <div className="flex gap-4 justify-center">
                <button onClick={() => { setSharePrompt(null); setIsOverlayOpen(false); }} className="bg-gray-200 text-gray-800 font-semibold py-3 px-6 rounded-full">Not now</button>
                <button onClick={confirmShareNow} className="bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold py-3 px-6 rounded-full">Share my artwork</button>
              </div>
            </div>
          </div>
        </CoLearningPromptBackdrop>
      )}

      {/* Art History Wall Modal - Similar to BookWallModal but for art history */}
      {selectedActivity && isOverlayOpen && (
        <ArtHistoryWallModal
          artworkKey={selectedActivity.artworkKey}
          artworkMeta={selectedActivity.artworkMeta}
          flowActivityMeta={selectedActivity.flowActivityMeta || null}
          submissions={filterSubmissions(selectedActivity.submissions || [])}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

// Art History Wall Modal Component
const ArtHistoryWallModal = ({ artworkKey, artworkMeta, flowActivityMeta, submissions, onClose }) => {
  const { user } = useAuth();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showUnshareConfirm, setShowUnshareConfirm] = useState(false);
  const [submissionToUnshare, setSubmissionToUnshare] = useState(null);
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const isFlowActivity = !!flowActivityMeta;

  const [reactionTotalsBySubmissionId, setReactionTotalsBySubmissionId] = useState({});
  const [isSortingSubmissions, setIsSortingSubmissions] = useState(false);

  const toMillis = (t) => {
    try {
      if (!t) return 0;
      if (typeof t?.toMillis === 'function') return t.toMillis();
      if (typeof t?.toDate === 'function') return t.toDate().getTime();
      if (t?._seconds) return t._seconds * 1000 + (t._nanoseconds || 0) / 1000000;
      if (t instanceof Date) return t.getTime();
      const d = new Date(t);
      return isNaN(d.getTime()) ? 0 : d.getTime();
    } catch (_) {
      return 0;
    }
  };

  // Sort submissions by: max reactions (total kudos) first, then latest contribution (createdAt desc).
  useEffect(() => {
    if (!(submissions || []).length) return;

    let cancelled = false;

    const run = async () => {
      setIsSortingSubmissions(true);
      try {
        const scores = await Promise.all(
          (submissions || []).map(async (submission) => {
            const reactionArtworkId = getReactionArtworkId(submission);
            if (!reactionArtworkId) return [submission.id, 0];
            try {
              const resp = await fetch(`${API_ENDPOINTS.getKudosCount}?artworkId=${encodeURIComponent(reactionArtworkId)}`);
              const data = await resp.json();
              const totalReactions = data?.success ? (data?.totalReactions ?? 0) : 0;
              return [submission.id, totalReactions];
            } catch (e) {
              return [submission.id, 0];
            }
          })
        );

        if (cancelled) return;

        const map = {};
        scores.forEach(([id, total]) => {
          map[id] = total;
        });
        setReactionTotalsBySubmissionId(map);
      } finally {
        if (!cancelled) setIsSortingSubmissions(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [submissions]);

  const sortedSubmissions = useMemo(() => {
    const list = [...(submissions || [])];
    list.sort((a, b) => {
      const ra = reactionTotalsBySubmissionId[a?.id] ?? 0;
      const rb = reactionTotalsBySubmissionId[b?.id] ?? 0;
      if (rb !== ra) return rb - ra;
      return toMillis(b?.createdAt) - toMillis(a?.createdAt);
    });
    return list;
  }, [submissions, reactionTotalsBySubmissionId]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (showUnshareConfirm) {
        e.preventDefault();
        setShowUnshareConfirm(false);
        setSubmissionToUnshare(null);
        return;
      }
      if (expandedSubmission) {
        e.preventDefault();
        setExpandedSubmission(null);
        return;
      }
      e.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showUnshareConfirm, expandedSubmission, onClose]);

  useEffect(() => {
    if (!expandedSubmission) return;
    const currentIndex = sortedSubmissions.findIndex((s) => s?.id === expandedSubmission?.id);
    const goPrev = () => {
      if (!sortedSubmissions.length) return;
      const nextIndex = currentIndex <= 0 ? sortedSubmissions.length - 1 : currentIndex - 1;
      setExpandedSubmission(sortedSubmissions[nextIndex]);
    };
    const goNext = () => {
      if (!sortedSubmissions.length) return;
      const nextIndex = currentIndex >= sortedSubmissions.length - 1 ? 0 : currentIndex + 1;
      setExpandedSubmission(sortedSubmissions[nextIndex]);
    };
    const onKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [expandedSubmission, sortedSubmissions]);

  const activeSubmission = expandedSubmission || sortedSubmissions[0] || null;
  const activeFlowMeta = activeSubmission?.flowActivityMeta || flowActivityMeta || null;
  const activeFlowSteps = Array.isArray(activeFlowMeta?.steps) ? activeFlowMeta.steps : [];
  /** One source of truth for the left “Activity” column (avoids duplicating title/objective). */
  const flowDisplayMeta = activeFlowMeta || flowActivityMeta || null;

  const wallTree = (
    <div {...coLearningWallBackdropProps} onClick={onClose}>
      <div
        className="flex h-[min(96vh,calc(100dvh-1rem))] w-full max-w-[calc(100vw-0.75rem)] flex-col overflow-hidden rounded-2xl border border-[#e8e0d4] bg-[#fffdf9] shadow-[0_24px_80px_rgba(45,35,25,0.12)] sm:max-w-[calc(100vw-1.25rem)] sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Compact header — activity dashboard style (no amber) */}
        <div className="flex-shrink-0 bg-white/95 backdrop-blur-sm border-b border-amber-200/50 shadow-sm max-h-[72px] md:max-h-[80px]">
          <div className="flex items-center justify-between py-2 md:py-2.5 h-full min-h-[52px] px-4 md:px-6">
            <h3 className="text-sm md:text-base font-semibold text-gray-800 truncate pr-2">{isFlowActivity ? (flowActivityMeta.title || 'Flow Activity') : (artworkMeta?.title || 'Art History Artwork')}</h3>
            <button onClick={onClose} className="p-1.5 md:p-2 rounded-full bg-stone-100/90 text-[#141210] hover:bg-stone-200/90 transition-colors flex-shrink-0" aria-label="Close">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content — one continuous sheet: activity | submissions (no separate grey/gold “boxes”) */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5 md:p-6">
          <div className="grid min-h-0 grid-cols-1 gap-0 lg:grid-cols-2 lg:divide-x lg:divide-[#e8e0d4]">
            <div className="flex min-h-0 flex-col pb-6 lg:min-h-[min(58vh,640px)] lg:pb-0 lg:pr-6">
              {isFlowActivity && flowDisplayMeta ? (
                <div className="rounded-2xl bg-[#f4efe6] p-5 ring-1 ring-[#e0d6c8] sm:p-6 md:p-7">
                  <p className="mb-3 text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8a6a44] sm:text-xs">
                    Reference artworks
                  </p>
                  <div className="grid grid-cols-2 gap-4 sm:gap-5">
                    {flowDisplayMeta.image1Url && (
                      <figure className="flex flex-col overflow-hidden rounded-xl bg-white/90 shadow-[inset_0_0_0_1px_rgba(232,224,212,0.9)]">
                        <div className="relative flex w-full min-h-[min(34vh,280px)] items-center justify-center sm:min-h-[min(38vh,320px)] md:min-h-[min(42vh,400px)]">
                          <SmartImage
                            src={flowDisplayMeta.image1Url}
                            alt={flowDisplayMeta.title1 || 'Artwork 1'}
                            className="max-h-[min(50vh,520px)] w-full object-contain"
                            containerClassName="flex h-full w-full items-center justify-center"
                            loading="eager"
                            fetchPriority="high"
                            decoding="sync"
                          />
                        </div>
                        {flowDisplayMeta.title1 && (
                          <figcaption className="px-2 py-2.5 text-center text-[11px] leading-snug text-[#5e5a54] sm:text-xs">
                            {flowDisplayMeta.title1}
                          </figcaption>
                        )}
                      </figure>
                    )}
                    {flowDisplayMeta.image2Url && (
                      <figure className="flex flex-col overflow-hidden rounded-xl bg-white/90 shadow-[inset_0_0_0_1px_rgba(232,224,212,0.9)]">
                        <div className="relative flex w-full min-h-[min(34vh,280px)] items-center justify-center sm:min-h-[min(38vh,320px)] md:min-h-[min(42vh,400px)]">
                          <SmartImage
                            src={flowDisplayMeta.image2Url}
                            alt={flowDisplayMeta.title2 || 'Artwork 2'}
                            className="max-h-[min(50vh,520px)] w-full object-contain"
                            containerClassName="flex h-full w-full items-center justify-center"
                            loading="eager"
                            fetchPriority="high"
                            decoding="sync"
                          />
                        </div>
                        {flowDisplayMeta.title2 && (
                          <figcaption className="px-2 py-2.5 text-center text-[11px] leading-snug text-[#5e5a54] sm:text-xs">
                            {flowDisplayMeta.title2}
                          </figcaption>
                        )}
                      </figure>
                    )}
                  </div>

                  <div className="mt-6 border-t border-[#dcd3c4] pt-6">
                    <h4 className="mb-3 text-center text-xs font-bold uppercase tracking-[0.12em] text-[#6b4e32] sm:text-sm">
                      Activity details
                    </h4>
                    <p className="text-center text-base font-semibold leading-snug text-[#141210] sm:text-lg">
                      {flowDisplayMeta.title || 'Flow activity'}
                    </p>
                    {flowDisplayMeta.objective && (
                      <p className="mt-3 text-center text-sm leading-relaxed text-[#4a433a] sm:text-[15px]">
                        {flowDisplayMeta.objective}
                      </p>
                    )}
                    {activeFlowSteps.length > 0 ? (
                      <div className="mt-4">
                        <h5 className="mb-2 text-center text-xs font-semibold uppercase tracking-wide text-[#8a6a44]">
                          Steps
                        </h5>
                        <ul className="max-h-[min(36vh,260px)] space-y-2.5 overflow-y-auto pr-1 text-left text-sm leading-snug text-[#141210] sm:max-h-[min(44vh,340px)]">
                          {activeFlowSteps.map((step, idx) => (
                            <li key={idx} className="flex gap-2.5 rounded-lg bg-white/50 px-2 py-1.5 sm:px-3">
                              <span className="shrink-0 font-semibold text-[#141210]">{idx + 1}.</span>
                              <span>{String(step)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : !isFlowActivity ? (
                <div className="rounded-2xl bg-[#f4efe6] p-5 ring-1 ring-[#e0d6c8] sm:p-6 md:p-7">
                  <h4 className="mb-3 text-center text-base font-bold text-[#5c4a38] sm:text-lg">Original Artwork</h4>
                  {resolveArtworkPreviewUrl(artworkMeta) && (
                    <SmartImage
                      src={resolveArtworkPreviewUrl(artworkMeta)}
                      alt={artworkMeta.title}
                      className="w-full rounded-xl bg-white/90 shadow-[inset_0_0_0_1px_rgba(232,224,212,0.9)]"
                      containerClassName="w-full"
                      loading="eager"
                      fetchPriority="high"
                      decoding="sync"
                      onError={addProxyFallbackOnError}
                    />
                  )}
                  <div className="mt-4 border-t border-[#dcd3c4] pt-4 text-center">
                    <p className="text-xs font-bold uppercase tracking-wide text-[#8a6a44]">Activity details</p>
                    <p className="mt-2 text-base font-semibold text-[#141210]">{artworkMeta?.title}</p>
                    <p className="mt-1 text-sm text-[#5e5a54]">{artworkMeta?.artist}</p>
                    {artworkMeta?.movement && (
                      <span className="mt-3 inline-block rounded-full bg-stone-200/90 px-2.5 py-1 text-xs text-[#141210]">
                        {artworkMeta.movement}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-600">Activity details are not available for this wall.</p>
              )}
            </div>

            <div className="flex min-h-0 flex-col pt-6 lg:min-h-[min(58vh,640px)] lg:pl-6 lg:pt-0">
              <h4 className="mb-3 text-base font-bold text-[#5c4a38] sm:text-lg">
                Submissions ({submissions.length})
              </h4>
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-0.5 lg:max-h-none">
                {sortedSubmissions.map((submission, index) => (
                  <div key={submission.id} className="rounded-xl border border-[#e8e0d4] bg-white p-3 shadow-sm sm:p-4">
                    {resolveArtworkPreviewUrl(submission) && (
                      <button
                        type="button"
                        onClick={() => setExpandedSubmission(submission)}
                        className="w-full text-left"
                        title="Open full screen"
                      >
                        <SmartImage
                          src={resolveArtworkPreviewUrl(submission)}
                          alt="User submission"
                          className="mb-2 w-full max-h-[min(62vh,720px)] rounded-lg object-contain sm:max-h-[min(68vh,780px)]"
                          containerClassName="w-full"
                          loading="eager"
                          fetchPriority="high"
                          decoding="sync"
                          onError={addProxyFallbackOnError}
                        />
                      </button>
                    )}
                    <div className="mt-2 space-y-1 text-center">
                      <p className="text-xs font-medium text-[#5e5a54]">
                        By {submission.userName || 'Anonymous'}
                      </p>
                    {submission.createdAt && (() => {
                      let date;
                      // Handle Firestore Timestamp object (from direct Firestore queries)
                      if (submission.createdAt?.toDate) {
                        date = submission.createdAt.toDate();
                      }
                      // Handle Firestore Timestamp serialized format from API (_seconds, _nanoseconds)
                      else if (submission.createdAt?._seconds) {
                        date = new Date(submission.createdAt._seconds * 1000 + (submission.createdAt._nanoseconds || 0) / 1000000);
                      }
                      // Handle ISO string or Date object
                      else if (submission.createdAt instanceof Date) {
                        date = submission.createdAt;
                      }
                      // Try parsing as Date
                      else {
                        date = new Date(submission.createdAt);
                      }
                      
                      // Check if date is valid
                      if (isNaN(date.getTime())) {
                        return null; // Don't render if invalid
                      }
                      
                      return (
                        <p className="text-xs text-[#8b867f]">
                          {date.toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </p>
                      );
                    })()}
                    </div>

                    {/* Positive-only reactions and unshare button */}
                    <div className="mt-2 flex items-center justify-between">
                      <ArtworkReactions
                        artworkId={getReactionArtworkId(submission)}
                        artistId={submission.uid || submission.userId}
                        enableComments={false}
                      />
                      {/* Owner unshare button */}
                      {(submission.uid === user?.uid || submission.userId === user?.uid) && (
                        <button
                          onClick={() => {
                            setSubmissionToUnshare(submission);
                            setShowUnshareConfirm(true);
                          }}
                          className="px-3 py-1 rounded-full bg-white text-[#141210] border border-[#e0d8cc] hover:bg-stone-50 text-xs"
                        >
                          Unshare
                        </button>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setExpandedSubmission(submission)}
                      className="mt-2 text-xs font-medium text-[#141210] hover:underline"
                    >
                      View full screen
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Unshare confirm modal */}
        {showUnshareConfirm && submissionToUnshare && (
          <div {...coLearningNestedBackdropProps}>
            <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-xl border border-[#e8e0d4]">
              <div className="px-6 py-5">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-stone-200 to-stone-300 text-[#141210] font-bold shadow mb-3">A</div>
                <h3 className={`text-xl mb-2 ${coLearningInkTitle}`}>Unshare from Co‑Learning?</h3>
                <p className={`mb-4 text-sm ${coLearningInkBody}`}>Unsharing will remove this artwork from the co-learning space. You can always share it again later.</p>
                <div className="flex gap-3 justify-end">
                  <button onClick={() => {
                    setShowUnshareConfirm(false);
                    setSubmissionToUnshare(null);
                  }} className={`px-4 py-2 rounded-full border border-[#e0d8cc] bg-white hover:bg-stone-50 ${coLearningInkBody}`}>Keep Public</button>
                  <button onClick={async () => {
                    try {
                      if (!submissionToUnshare || !user) return;
                      await updateDoc(doc(db, 'artHistorySharedArtworks', submissionToUnshare.id), { 
                        visibility: 'private', 
                        updatedAt: serverTimestamp() 
                      });
                      
                      setShowUnshareConfirm(false);
                      setSubmissionToUnshare(null);
                      // The real-time listener will automatically update the submissions list
                    } catch (e) {
                      console.error('Failed to unshare art history artwork:', e);
                      alert('Failed to unshare artwork. Please try again.');
                    }
                  }} className="px-4 py-2 rounded-full text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600">Unshare</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Full-screen user submission preview */}
        {expandedSubmission && (
          <div
            role="presentation"
            {...coLearningExpandedBackdropProps}
            onClick={() => setExpandedSubmission(null)}
          >
            <div
              className="relative z-[1] mx-auto flex h-full w-full max-w-7xl items-center justify-center gap-2 px-1 sm:gap-4 sm:px-3"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setExpandedSubmission(null)}
                className="absolute right-3 top-3 z-[510] flex h-11 w-11 items-center justify-center rounded-full text-[#141210] shadow-[0_8px_28px_rgba(0,0,0,0.12)] transition-transform hover:scale-105 sm:right-5 sm:top-5 sm:h-12 sm:w-12"
                style={{
                  background: 'rgba(255,255,255,0.88)',
                  backdropFilter: 'blur(14px)',
                  WebkitBackdropFilter: 'blur(14px)',
                }}
                aria-label="Close expanded view"
              >
                <FaTimes className="h-5 w-5" />
              </button>
              {sortedSubmissions.length > 1 && (
                <button
                  type="button"
                  onClick={() => {
                    const idx = sortedSubmissions.findIndex((s) => s?.id === expandedSubmission?.id);
                    const nextIndex = idx <= 0 ? sortedSubmissions.length - 1 : idx - 1;
                    setExpandedSubmission(sortedSubmissions[nextIndex]);
                  }}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/55 bg-white/[0.72] text-[#141210] shadow-[0_4px_20px_rgba(0,0,0,0.12)] backdrop-blur-md transition-all hover:scale-[1.08] active:scale-95 sm:h-12 sm:w-12"
                  style={{ touchAction: 'manipulation', WebkitBackdropFilter: 'blur(12px)' }}
                  aria-label="Previous submission"
                >
                  <FaChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              )}
              <div className="relative flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center pb-[4.5rem] sm:pb-20">
                {resolveArtworkPreviewUrl(expandedSubmission) ? (
                  <img
                    src={resolveArtworkPreviewUrl(expandedSubmission)}
                    alt="Expanded user submission"
                    className="max-h-[min(78dvh,88vh,920px)] max-w-full object-contain drop-shadow-2xl"
                  />
                ) : (
                  <div className="flex max-h-[min(60vh,520px)] min-h-[40vh] w-full max-w-2xl items-center justify-center rounded-2xl border border-white/30 bg-white/90 text-[#141210]">
                    No image available
                  </div>
                )}
                <div className="pointer-events-none absolute bottom-3 left-0 right-0 z-[510] flex justify-center px-2 sm:bottom-5 sm:px-4">
                  <div
                    className="pointer-events-auto max-w-full rounded-2xl px-4 py-2 text-center text-xs font-medium text-[#141210] ring-1 ring-white/40 sm:px-5 sm:py-2.5 sm:text-sm"
                    style={{
                      background: 'rgba(255,255,255,0.88)',
                      backdropFilter: 'blur(14px)',
                      WebkitBackdropFilter: 'blur(14px)',
                      boxShadow: '0 8px 28px rgba(0,0,0,0.08)',
                    }}
                  >
                    {Math.max(1, sortedSubmissions.findIndex((s) => s?.id === expandedSubmission?.id) + 1)} of{' '}
                    {sortedSubmissions.length}
                    {sortedSubmissions.length > 1 ? ' · Left/right to browse' : ''} · By {expandedSubmission.userName || 'Anonymous'}
                  </div>
                </div>
              </div>
              {sortedSubmissions.length > 1 && (
                <button
                  type="button"
                  onClick={() => {
                    const idx = sortedSubmissions.findIndex((s) => s?.id === expandedSubmission?.id);
                    const nextIndex = idx >= sortedSubmissions.length - 1 ? 0 : idx + 1;
                    setExpandedSubmission(sortedSubmissions[nextIndex]);
                  }}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/55 bg-white/[0.72] text-[#141210] shadow-[0_4px_20px_rgba(0,0,0,0.12)] backdrop-blur-md transition-all hover:scale-[1.08] active:scale-95 sm:h-12 sm:w-12"
                  style={{ touchAction: 'manipulation', WebkitBackdropFilter: 'blur(12px)' }}
                  aria-label="Next submission"
                >
                  <FaChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(wallTree, document.body) : wallTree;
};

export default ArtHistoryCoLearningSpace;


